import Modal from 'tio-ui/components/modal';
import Footer from 'tio-ui/components/footer';
import { formatDuration } from '../../utils/date/format-duration';
import { modifier } from 'ember-modifier';
import { registerDestructor } from '@ember/destroyable';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import type Owner from '@ember/owner';

export interface SessionExpiringDialogSignature {
  Args: {
    isSessionExpiring: boolean;
    expireAt: number | null;
    onRenew: () => void;
    onLogout: () => void;
  };
}

export default class SessionExpiringDialogComponent extends Component<SessionExpiringDialogSignature> {
  #intervalId?: ReturnType<typeof setInterval>;
  // Keyed on `expireAt` (not a boolean) so a new warn cycle re-surfaces the
  // modal even if a failed renewal never clears `isSessionExpiring`.
  @tracked dismissedForExpireAt: number | null = null;
  @tracked timeRemainingFormatted = '';

  constructor(owner: Owner, args: SessionExpiringDialogSignature['Args']) {
    super(owner, args);
    registerDestructor(this, this.teardown);
  }

  get isShowing() {
    if (!this.args.isSessionExpiring) return false;
    if (this.dismissedForExpireAt !== null && this.dismissedForExpireAt === this.args.expireAt) {
      return false;
    }
    return true;
  }

  teardown = () => {
    this.#stopCountdown();
  };

  watchShowing = modifier((_element: HTMLElement, [isShowing]: [boolean]) => {
    if (isShowing) {
      this.startCountdown();
    } else {
      this.#stopCountdown();
    }
  });

  startCountdown = () => {
    if (this.#intervalId) return;

    this.updateTimeRemaining();

    this.#intervalId = setInterval(() => {
      this.updateTimeRemaining();
    }, 1000);
  };

  updateTimeRemaining = () => {
    if (!this.args.expireAt) {
      this.timeRemainingFormatted = '';
      return;
    }

    const timeRemaining = this.args.expireAt - Date.now();

    if (timeRemaining <= 0) {
      this.timeRemainingFormatted = '';
      this.#stopCountdown();
      return;
    }

    const timeRemainingSeconds = Math.floor(timeRemaining / 1000);
    this.timeRemainingFormatted = formatDuration(timeRemainingSeconds);
  };

  close = () => {
    this.dismissedForExpireAt = this.args.expireAt;
    this.#stopCountdown();
  };

  renew = () => {
    this.args.onRenew();
    this.close();
  };

  logout = () => {
    this.args.onLogout();
    this.close();
  };

  #stopCountdown() {
    if (this.#intervalId) {
      clearInterval(this.#intervalId);
      this.#intervalId = undefined;
    }
  }

  <template>
    <div {{this.watchShowing this.isShowing}}>
      <Modal
        @isOpen={{this.isShowing}}
        @onClose={{this.close}}
        @closeOnOutsideClick={{false}}
        @closeOnEscapeKey={{false}}
        @allowCloseButton={{false}}
      >
        <:header>{{t "auth.session_expiring.title"}}</:header>
        <:body>
          <span data-test-session-expiring-message>
            {{t "auth.session_expiring.message" time=this.timeRemainingFormatted}}
          </span>
        </:body>
        <:footer>
          <Footer
            @isRunning={{false}}
            @onSubmit={{this.renew}}
            @onCancel={{this.logout}}
            @submitText={{t "header.user_menu.keep_alive"}}
            @cancelText={{t "header.user_menu.logout"}}
          />
        </:footer>
      </Modal>
    </div>
  </template>
}
